import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, ChevronDown, Send, CheckCircle, Zap } from 'lucide-react';
import { ReactComponent as Logomark } from 'assets/images/logos/Logomark.svg';
import AccountDefaultImage from 'assets/images/avatars/default-avatar.jpg';
import Spinner from 'components/common/Spinner';
import ReactMarkdown from 'react-markdown';
import { useTheme } from 'contexts/ThemeContext';
import { getAccessToken } from 'utils/auth/auth';
import {
  startSession,
  sendChatMessage,
  confirmRules,
  ChatMessage,
  AccountProfile,
  ProposedRules,
} from 'services/chatbot/chatbotService';

interface Account {
  _id: string;
  username: string;
  name: string;
  profile_picture_base64?: string;
}

const formatCount = (num: number): string => {
  if (num >= 1000000) return (num / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'k';
  return String(num);
};


const NewRule: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  // Receive account data via navigation state
  const navState = (location.state as { selectedAccount?: Account; accounts?: Account[]; fromDashboard?: boolean }) || {};
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(navState.selectedAccount || null);
  const [accounts] = useState<Account[]>(navState.accounts || []);
  const [showRulesRequiredModal, setShowRulesRequiredModal] = useState(!!navState.fromDashboard);

  // Session state
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [status, setStatus] = useState<string>('active');
  const [accountProfile, setAccountProfile] = useState<AccountProfile | null>(null);
  const [proposedRules, setProposedRules] = useState<ProposedRules | null>(null);

  // Loading state
  const [isStarting, setIsStarting] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);

  // Streaming state
  const [isStreaming, setIsStreaming] = useState(false);
  const [currentToolStatus, setCurrentToolStatus] = useState<string | null>(null);
  const [wsConnected, setWsConnected] = useState(false);

  // UI state
  const { isDark } = useTheme();
  const [isAccountDropdownOpen, setIsAccountDropdownOpen] = useState(false);
  const [inputMessage, setInputMessage] = useState('');

  // Refs
  const wsRef = useRef<WebSocket | null>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mobileDropdownRef = useRef<HTMLDivElement>(null);
  const streamingContentRef = useRef('');
  const sessionStartedRef = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const inDesktop = dropdownRef.current?.contains(target);
      const inMobile = mobileDropdownRef.current?.contains(target);
      if (!inDesktop && !inMobile) {
        setIsAccountDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Auto-scroll to bottom
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, currentToolStatus, isLoading]);

  // Redirect if no account
  useEffect(() => {
    if (!selectedAccount) {
      navigate('/rules');
    }
  }, [selectedAccount, navigate]);

  // WebSocket message handler
  interface WsMessage {
    type: string;
    content?: string;
    full_content?: string;
    status_message?: string;
    [key: string]: unknown;
  }
  const handleWsMessage = useCallback((data: WsMessage) => {
    switch (data.type) {
      case 'connection_established':
        setWsConnected(true);
        break;
      case 'chatbot_stream_start':
        setIsStreaming(true);
        streamingContentRef.current = '';
        setCurrentToolStatus(null);
        setMessages(prev => [...prev, { role: 'assistant', content: '', timestamp: new Date().toISOString() }]);
        break;
      case 'chatbot_stream_chunk':
        streamingContentRef.current += data.content ?? '';
        const accumulated = streamingContentRef.current;
        setMessages(prev => {
          const updated = [...prev];
          if (updated.length > 0 && updated[updated.length - 1].role === 'assistant') {
            updated[updated.length - 1] = { ...updated[updated.length - 1], content: accumulated };
          }
          return updated;
        });
        break;
      case 'chatbot_stream_end':
        setIsStreaming(false);
        streamingContentRef.current = '';
        const fullContent = data.full_content;
        setMessages(prev => {
          const updated = [...prev];
          if (updated.length > 0 && updated[updated.length - 1].role === 'assistant') {
            updated[updated.length - 1] = { ...updated[updated.length - 1], content: fullContent ?? '' };
          }
          return updated;
        });
        break;
      case 'chatbot_tool_status':
        setCurrentToolStatus(data.status_message ?? null);
        break;
    }
  }, []);

  // Connect WebSocket
  const connectWebSocket = useCallback(() => {
    const token = getAccessToken();
    const wsBaseUrl = process.env.REACT_APP_FAMOUS_CLICKER_WEBSOCKET_URL;
    if (!wsBaseUrl || !token) return;

    wsRef.current = new WebSocket(`${wsBaseUrl}?token=${token}&type=chatbot`);

    wsRef.current.onmessage = (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data);
        handleWsMessage(data);
      } catch {
        // Ignore invalid JSON
      }
    };

    wsRef.current.onclose = () => {
      setWsConnected(false);
    };

    wsRef.current.onerror = () => {
      setWsConnected(false);
    };
  }, [handleWsMessage]);

  // Start session handler
  const handleStartSession = useCallback(async () => {
    if (!selectedAccount || sessionStartedRef.current) return;
    sessionStartedRef.current = true;
    setIsStarting(true);

    try {
      const response = await startSession(selectedAccount._id);
      // Handle both response shapes: response.data (axios) or response directly (if service returns .data)
      const data = response?.data ?? response;
      if (data?.session_id) {
        setSessionId(data.session_id);
      }
      if (data?.account_analysis) {
        setAccountProfile(data.account_analysis);
      }
      // If streamed via WS, the message was already added by WS handlers
      // If not streamed, add it from the REST response
      if (data?.message && !data.streamed) {
        const msg = typeof data.message === 'string'
          ? { role: 'assistant' as const, content: data.message, timestamp: new Date().toISOString() }
          : data.message;
        setMessages(prev => [...prev, msg]);
      }
      // When REST response arrives, streaming is done
      setIsStreaming(false);
    } catch {
      // Error already shown via toast in service
    } finally {
      setIsStarting(false);
      setIsStreaming(false);
    }
  }, [selectedAccount]);

  // Mount: connect WS, then start session
  useEffect(() => {
    if (!selectedAccount) return;
    connectWebSocket();

    // Fallback: if WS doesn't connect within 3s, start session anyway
    const fallbackTimer = setTimeout(() => {
      if (!sessionStartedRef.current) {
        handleStartSession();
      }
    }, 3000);

    return () => {
      clearTimeout(fallbackTimer);
      if (wsRef.current) wsRef.current.close();
    };
  }, [selectedAccount, connectWebSocket, handleStartSession]);

  // When WS connects, start session
  useEffect(() => {
    if (wsConnected && !sessionStartedRef.current) {
      handleStartSession();
    }
  }, [wsConnected, handleStartSession]);

  // Send message
  const handleSendMessage = async () => {
    if (!inputMessage.trim() || !sessionId || isLoading) return;

    const userMsg = inputMessage.trim();
    setInputMessage('');
    setMessages(prev => [...prev, { role: 'user', content: userMsg, timestamp: new Date().toISOString() }]);
    setIsLoading(true);
    setCurrentToolStatus(null);

    try {
      const response = await sendChatMessage(sessionId, userMsg);
      const data = response?.data ?? response;
      // If not streamed, add the assistant message
      if (data?.message && !data.streamed) {
        const msg = typeof data.message === 'string'
          ? { role: 'assistant' as const, content: data.message, timestamp: new Date().toISOString() }
          : data.message;
        setMessages(prev => [...prev, msg]);
      }
      if (data?.status) {
        setStatus(data.status);
      }
      if (data?.proposed_rules) {
        setProposedRules(data.proposed_rules);
      }
    } catch {
      // Error already shown via toast
    } finally {
      setIsLoading(false);
      setIsStreaming(false);
      setCurrentToolStatus(null);
    }
  };

  // Confirm or reject rules
  const handleConfirmRules = async (confirmed: boolean) => {
    if (!sessionId) return;
    setIsConfirming(true);

    try {
      const response = await confirmRules(sessionId, confirmed);
      const data = response?.data ?? response;
      if (confirmed) {
        setMessages(prev => [...prev, { role: 'assistant', content: data?.message || 'Rules created successfully!', timestamp: new Date().toISOString() }]);
        setStatus('rules_created');
        setProposedRules(null);
        setTimeout(() => navigate('/rules'), 2000);
      } else {
        setMessages(prev => [...prev, { role: 'assistant', content: data?.message || 'Let\'s refine your targeting strategy.', timestamp: new Date().toISOString() }]);
        setStatus('active');
        setProposedRules(null);
      }
    } catch {
      // Error already shown via toast
    } finally {
      setIsConfirming(false);
    }
  };

  // Handle Enter key
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Account switch
  const handleAccountSelect = (account: Account) => {
    setSelectedAccount(account);
    setIsAccountDropdownOpen(false);
    // Reset session for new account
    if (wsRef.current) wsRef.current.close();
    setSessionId(null);
    setMessages([]);
    setStatus('active');
    setAccountProfile(null);
    setProposedRules(null);
    setIsStarting(true);
    sessionStartedRef.current = false;
    connectWebSocket();
  };

  // Color tokens
  const containerBg = isDark ? '#1D293D' : '#F8FAFC';
  const mainBg = isDark ? '#0F172B' : '#FFFFFF';
  const mainShadow = isDark ? '0px 1px 2px rgba(2, 6, 24, 0.5)' : '0px 1px 2px rgba(43, 127, 255, 0.1)';
  const headerBorder = isDark ? '#1D293D' : '#F1F5F9';
  const titleColor = isDark ? '#E2E8F0' : '#314158';
  const subtitleColor = isDark ? '#90A1B9' : '#62748E';
  const buttonBg = mainBg;
  const buttonBorder = isDark ? '#314158' : '#E2E8F0';
  const buttonIconColor = isDark ? '#CAD5E2' : '#45556C';
  const userBubbleBg = isDark ? '#162456' : '#EFF6FF';
  const profileCardBg = isDark ? '#1D293D' : '#F8FAFC';
  const profileCardBorder = isDark ? '#1D293D' : '#F1F5F9';
  const usernameColor = isDark ? '#51A2FF' : '#2B7FFF';
  const inputBorder = isDark ? '#314158' : '#E2E8F0';
  const inputShadow = isDark
    ? '0px 4px 6px -1px rgba(2, 6, 24, 0.5), 0px 2px 4px -1px rgba(0, 0, 0, 0.06)'
    : '0px 4px 6px -1px rgba(43, 127, 255, 0.1), 0px 2px 4px -1px rgba(0, 0, 0, 0.06)';
  const ruleCardBorder = isDark ? '#314158' : '#E2E8F0';

  if (!selectedAccount) return null;

  // Find the index of the first agent message that has an account profile card after it
  const firstAgentMsgIndex = messages.findIndex(m => m.role === 'assistant');

  const canSend = inputMessage.trim().length > 0 && !isLoading && status !== 'rules_proposed' && status !== 'rules_created' && status !== 'completed';

  return (
    <div
      className="flex flex-col items-start font-geist p-0 lg:pt-2 lg:pr-2 lg:pb-2 lg:pl-0"
      style={{ background: containerBg, flex: 1, minHeight: 0, overflow: 'hidden', gap: '8px' }}
    >
      <div
        className="flex flex-col flex-1 w-full rounded-none lg:rounded-2xl"
        style={{ background: mainBg, boxShadow: mainShadow, overflow: 'hidden', minHeight: 0 }}
      >
        {/* Header */}
        <div
          className="flex flex-col gap-2 flex-shrink-0"
          style={{ padding: '12px 16px', borderBottom: `1px solid ${headerBorder}`, background: mainBg }}
        >
          <div className="flex items-center" style={{ gap: '8px' }}>
            {/* Back button */}
            <button
              className="flex items-center gap-1 transition-opacity hover:opacity-80"
              style={{
                padding: '8px 12px',
                background: buttonBg,
                border: `1px solid ${buttonBorder}`,
                borderRadius: '8px',
                cursor: 'pointer',
              }}
              onClick={() => navigate('/rules')}
            >
              <ArrowLeft size={20} color={buttonIconColor} />
              <span className="text-sm font-medium" style={{ color: titleColor }}>
                {t('rules.chatbot.back', 'Back')}
              </span>
            </button>

            {/* Title */}
            <h1
              className="font-bold flex-1"
              style={{ fontSize: '20px', lineHeight: '28px', color: titleColor, margin: 0, whiteSpace: 'nowrap' }}
            >
              {t('rules.chatbot.newRule', 'New rule')}
            </h1>

            {/* Account selector — desktop only */}
            <div className="relative hidden sm:block" ref={dropdownRef}>
              <button
                className="flex items-center gap-1 transition-opacity hover:opacity-80"
                style={{
                  padding: '8px 12px',
                  background: buttonBg,
                  border: `1px solid ${buttonBorder}`,
                  borderRadius: '8px',
                  cursor: accounts.length > 1 ? 'pointer' : 'default',
                }}
                onClick={() => accounts.length > 1 && setIsAccountDropdownOpen(!isAccountDropdownOpen)}
              >
                <img
                  src={selectedAccount.profile_picture_base64 || AccountDefaultImage}
                  alt={selectedAccount.username}
                  className="flex-shrink-0 object-cover"
                  style={{ width: '20px', height: '20px', borderRadius: '12px' }}
                  onError={(e) => { (e.target as HTMLImageElement).src = AccountDefaultImage; }}
                />
                <span className="text-sm font-medium" style={{ color: titleColor }}>
                  {selectedAccount.username}
                </span>
                {accounts.length > 1 && (
                  <ChevronDown
                    size={20}
                    color={buttonIconColor}
                    className={`transition-transform ${isAccountDropdownOpen ? 'rotate-180' : ''}`}
                  />
                )}
              </button>

              {isAccountDropdownOpen && accounts.length > 1 && (
                <div
                  className="absolute right-0 mt-2 z-50 max-h-60 overflow-y-auto"
                  style={{
                    width: '220px',
                    background: mainBg,
                    border: `1px solid ${buttonBorder}`,
                    borderRadius: '8px',
                    boxShadow: isDark
                      ? '0px 10px 15px -3px rgba(2, 6, 24, 0.5)'
                      : '0px 10px 15px -3px rgba(43, 127, 255, 0.1)',
                  }}
                >
                  {accounts.map((account) => (
                    <div
                      key={account._id}
                      className="flex items-center gap-2 cursor-pointer transition-all"
                      style={{
                        padding: '8px 12px',
                        background: selectedAccount?._id === account._id
                          ? (isDark ? '#162456' : '#EFF6FF')
                          : 'transparent',
                      }}
                      onMouseEnter={(e) => {
                        if (selectedAccount?._id !== account._id) {
                          e.currentTarget.style.background = isDark ? '#1D293D' : '#F8FAFC';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (selectedAccount?._id !== account._id) {
                          e.currentTarget.style.background = 'transparent';
                        }
                      }}
                      onClick={() => handleAccountSelect(account)}
                    >
                      <img
                        src={account.profile_picture_base64 || AccountDefaultImage}
                        alt={account.username}
                        className="flex-shrink-0 object-cover"
                        style={{ width: '20px', height: '20px', borderRadius: '12px' }}
                        onError={(e) => { (e.target as HTMLImageElement).src = AccountDefaultImage; }}
                      />
                      <span className="flex-1 text-sm font-medium truncate" style={{ color: titleColor }}>
                        {account.username}
                      </span>
                      {selectedAccount?._id === account._id && (
                        <CheckCircle size={16} color={isDark ? '#51A2FF' : '#2B7FFF'} />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Row 2 (mobile only): Account dropdown */}
          <div className="sm:hidden relative" ref={mobileDropdownRef}>
            <button
              className="flex items-center gap-1 transition-opacity hover:opacity-80 w-full"
              style={{
                padding: '8px 12px',
                background: buttonBg,
                border: `1px solid ${buttonBorder}`,
                borderRadius: '8px',
                cursor: accounts.length > 1 ? 'pointer' : 'default',
              }}
              onClick={() => accounts.length > 1 && setIsAccountDropdownOpen(!isAccountDropdownOpen)}
            >
              <img
                src={selectedAccount.profile_picture_base64 || AccountDefaultImage}
                alt={selectedAccount.username}
                className="flex-shrink-0 object-cover"
                style={{ width: '20px', height: '20px', borderRadius: '12px' }}
                onError={(e) => { (e.target as HTMLImageElement).src = AccountDefaultImage; }}
              />
              <span className="text-sm font-medium" style={{ color: titleColor, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0, textAlign: 'left' }}>
                {selectedAccount.username}
              </span>
              {accounts.length > 1 && (
                <ChevronDown
                  size={20}
                  color={buttonIconColor}
                  className={`transition-transform ${isAccountDropdownOpen ? 'rotate-180' : ''}`}
                />
              )}
            </button>

            {isAccountDropdownOpen && accounts.length > 1 && (
              <div
                className="absolute left-0 mt-2 z-50 max-h-60 overflow-y-auto w-full"
                style={{
                  background: mainBg,
                  border: `1px solid ${buttonBorder}`,
                  borderRadius: '8px',
                  boxShadow: isDark
                    ? '0px 10px 15px -3px rgba(2, 6, 24, 0.5)'
                    : '0px 10px 15px -3px rgba(43, 127, 255, 0.1)',
                }}
              >
                {accounts.map((account) => (
                  <div
                    key={account._id}
                    className="flex items-center gap-2 cursor-pointer transition-all"
                    style={{
                      padding: '8px 12px',
                      background: selectedAccount?._id === account._id
                        ? (isDark ? '#162456' : '#EFF6FF')
                        : 'transparent',
                    }}
                    onMouseEnter={(e) => {
                      if (selectedAccount?._id !== account._id) {
                        e.currentTarget.style.background = isDark ? '#1D293D' : '#F8FAFC';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedAccount?._id !== account._id) {
                        e.currentTarget.style.background = 'transparent';
                      }
                    }}
                    onClick={() => handleAccountSelect(account)}
                  >
                    <img
                      src={account.profile_picture_base64 || AccountDefaultImage}
                      alt={account.username}
                      className="flex-shrink-0 object-cover"
                      style={{ width: '20px', height: '20px', borderRadius: '12px' }}
                      onError={(e) => { (e.target as HTMLImageElement).src = AccountDefaultImage; }}
                    />
                    <span className="flex-1 text-sm font-medium truncate" style={{ color: titleColor }}>
                      {account.username}
                    </span>
                    {selectedAccount?._id === account._id && (
                      <CheckCircle size={16} color={isDark ? '#51A2FF' : '#2B7FFF'} />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Chat Area + Input */}
        <div className="flex flex-col flex-1" style={{ overflow: 'hidden', minHeight: 0 }}>
          {/* Scrollable chat area */}
          <div className={`flex-1 overflow-y-auto ${isDark ? 'scrollbar-themed-dark' : 'scrollbar-themed-light'}`} style={{ padding: '16px', minHeight: 0 }}>
            <div className="flex flex-col" style={{ paddingBottom: '80px' }}>
              {isStarting ? (
                <div className="relative" style={{ paddingLeft: '32px' }}>
                  <Logomark style={{ width: '24px', height: '24px', position: 'absolute', left: 0, top: 0 }} />
                  <div className="flex items-center gap-2">
                    <Spinner size="small" />
                    <span style={{ fontSize: '16px', color: subtitleColor, fontWeight: 400 }}>
                      {t('rules.chatbot.analyzingAccount', 'Analyzing your account...')}
                    </span>
                  </div>
                </div>
              ) : (
                messages.map((msg, idx) => {
                  const isAgent = msg.role === 'assistant';
                  const showLogomark = isAgent && (idx === 0 || messages[idx - 1]?.role !== 'assistant');
                  const showProfileCard = isAgent && idx === firstAgentMsgIndex && accountProfile;

                  if (isAgent) {
                    return (
                      <React.Fragment key={idx}>
                        {/* Agent bubble: text + optional profile card, gap 8px between them */}
                        <div
                          className="relative flex flex-col"
                          style={{ paddingLeft: '32px', gap: '8px', marginBottom: '24px' }}
                        >
                          {/* Logomark positioned absolutely at left: -32px from content */}
                          {showLogomark && (
                            <Logomark style={{ width: '24px', height: '24px', position: 'absolute', left: 0, top: 0 }} />
                          )}

                          {/* Agent message text */}
                          <div className="chatbot-markdown" style={{ fontSize: '16px', lineHeight: '24px', color: titleColor, fontWeight: 400 }}>
                            <ReactMarkdown>{msg.content}</ReactMarkdown>
                          </div>

                          {/* Account profile card after first agent message */}
                          {showProfileCard && (
                            <div
                              className="flex flex-col"
                              style={{
                                background: profileCardBg,
                                border: `1px solid ${profileCardBorder}`,
                                borderRadius: '8px',
                                padding: '12px',
                                gap: '8px',
                              }}
                            >
                              <div className="flex items-start" style={{ gap: '8px' }}>
                                <img
                                  src={selectedAccount.profile_picture_base64 || AccountDefaultImage}
                                  alt={accountProfile!.username}
                                  className="flex-shrink-0 object-cover"
                                  style={{ width: '40px', height: '40px', borderRadius: '12px' }}
                                  onError={(e) => { (e.target as HTMLImageElement).src = AccountDefaultImage; }}
                                />
                                <div className="flex flex-col flex-1 min-w-0">
                                  <span style={{ fontSize: '14px', lineHeight: '20px', fontWeight: 500, color: usernameColor }}>
                                    @{accountProfile!.username}
                                  </span>
                                  <span style={{ fontSize: '14px', lineHeight: '20px', fontWeight: 400, color: subtitleColor }}>
                                    {accountProfile!.bio || accountProfile!.full_name}
                                  </span>
                                </div>
                              </div>
                              <div className="flex" style={{ gap: '16px' }}>
                                <div className="flex items-center" style={{ gap: '4px' }}>
                                  <span style={{ fontSize: '14px', lineHeight: '20px', fontWeight: 500, color: titleColor }}>
                                    {formatCount(accountProfile!.followers_count)}
                                  </span>
                                  <span style={{ fontSize: '14px', lineHeight: '20px', fontWeight: 400, color: subtitleColor }}>
                                    {t('rules.chatbot.followers', 'Followers')}
                                  </span>
                                </div>
                                <div className="flex items-center" style={{ gap: '4px' }}>
                                  <span style={{ fontSize: '14px', lineHeight: '20px', fontWeight: 500, color: titleColor }}>
                                    {formatCount(accountProfile!.following_count)}
                                  </span>
                                  <span style={{ fontSize: '14px', lineHeight: '20px', fontWeight: 400, color: subtitleColor }}>
                                    {t('rules.chatbot.following', 'Following')}
                                  </span>
                                </div>
                                <div className="flex items-center" style={{ gap: '4px' }}>
                                  <span style={{ fontSize: '14px', lineHeight: '20px', fontWeight: 500, color: titleColor }}>
                                    {formatCount(accountProfile!.media_count)}
                                  </span>
                                  <span style={{ fontSize: '14px', lineHeight: '20px', fontWeight: 400, color: subtitleColor }}>
                                    {t('rules.chatbot.posts', 'Posts')}
                                  </span>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </React.Fragment>
                    );
                  }

                  // User message — right-aligned
                  return (
                    <div className="flex justify-end" style={{ paddingLeft: '32px', marginBottom: '24px' }} key={idx}>
                      <div
                        style={{
                          background: userBubbleBg,
                          borderRadius: '16px',
                          padding: '16px',
                          fontSize: '16px',
                          lineHeight: '24px',
                          fontWeight: 400,
                          color: titleColor,
                          maxWidth: '80%',
                        }}
                      >
                        {msg.content}
                      </div>
                    </div>
                  );
                })
              )}

              {/* Tool status indicator */}
              {currentToolStatus && (
                <div className="flex items-center" style={{ paddingLeft: '32px', gap: '8px', marginBottom: '16px' }}>
                  <Spinner size="small" />
                  <span style={{ fontSize: '14px', lineHeight: '20px', fontWeight: 400, color: subtitleColor, fontStyle: 'italic' }}>
                    {currentToolStatus}
                  </span>
                </div>
              )}

              {/* Typing indicator (loading, not streaming) */}
              {isLoading && !isStreaming && !currentToolStatus && (
                <div className="relative" style={{ paddingLeft: '32px', marginBottom: '16px' }}>
                  <Logomark style={{ width: '24px', height: '24px', position: 'absolute', left: 0, top: 0 }} />
                  <div className="flex items-center" style={{ gap: '6px', padding: '8px 0' }}>
                    {[0, 1, 2].map(i => (
                      <div
                        key={i}
                        style={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          background: subtitleColor,
                          animation: 'chatBounce 1.4s infinite ease-in-out both',
                          animationDelay: `${i * 0.16}s`,
                        }}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Rule proposal card with action buttons */}
              {proposedRules && status === 'rules_proposed' && (
                <div style={{ paddingLeft: '32px', marginBottom: '24px' }}>
                  <div
                    className="flex flex-col"
                    style={{
                      background: mainBg,
                      border: `1px solid ${ruleCardBorder}`,
                      borderRadius: '8px',
                      padding: '16px',
                      gap: '16px',
                    }}
                  >
                    {/* Rule info row */}
                    <div className="flex items-center justify-between">
                      <div className="flex flex-col" style={{ gap: '4px' }}>
                        <span style={{ fontSize: '16px', lineHeight: '24px', fontWeight: 500, color: titleColor }}>
                          {proposedRules.rule_name}
                        </span>
                        <span style={{ fontSize: '14px', lineHeight: '20px', fontWeight: 400, color: subtitleColor }}>
                          {proposedRules.rule.length} {t('rules.criteriaLabel', 'criteria')}
                        </span>
                      </div>
                      <span
                        style={{
                          fontSize: '12px',
                          lineHeight: '16px',
                          fontWeight: 500,
                          background: isDark ? '#162456' : '#EFF6FF',
                          border: `1px solid ${isDark ? '#155DFC' : '#8EC5FF'}`,
                          color: isDark ? '#8EC5FF' : '#155DFC',
                          borderRadius: '100px',
                          padding: '2px 6px',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {t('rules.status.readyToActivate', 'Ready to activate')}
                      </span>
                    </div>

                    {/* Action buttons */}
                    <div className="flex items-center" style={{ gap: '12px' }}>
                      <button
                        onClick={() => handleConfirmRules(true)}
                        disabled={isConfirming}
                        className="flex items-center justify-center transition-opacity hover:opacity-90"
                        style={{
                          background: 'linear-gradient(90deg, #2B7FFF 0%, #AD46FF 100%)',
                          borderRadius: '8px',
                          padding: '8px 16px',
                          border: 'none',
                          cursor: isConfirming ? 'default' : 'pointer',
                          opacity: isConfirming ? 0.5 : 1,
                        }}
                      >
                        <span style={{ fontSize: '14px', lineHeight: '20px', fontWeight: 500, color: '#FFFFFF' }}>
                          {isConfirming ? t('common.loading', 'Loading...') : t('rules.chatbot.createRules', 'Create Rules')}
                        </span>
                      </button>
                      <button
                        onClick={() => handleConfirmRules(false)}
                        disabled={isConfirming}
                        className="flex items-center justify-center transition-opacity hover:opacity-80"
                        style={{
                          background: 'transparent',
                          border: `1px solid ${buttonBorder}`,
                          borderRadius: '8px',
                          padding: '8px 16px',
                          cursor: isConfirming ? 'default' : 'pointer',
                          opacity: isConfirming ? 0.5 : 1,
                        }}
                      >
                        <span style={{ fontSize: '14px', lineHeight: '20px', fontWeight: 500, color: titleColor }}>
                          {t('rules.chatbot.refine', 'Refine')}
                        </span>
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div ref={chatEndRef} />
            </div>
          </div>

          {/* Input area */}
          <div style={{ padding: '0 24px 24px 24px' }}>
            <div
              className="flex items-center"
              style={{
                background: mainBg,
                border: `1px solid ${inputBorder}`,
                boxShadow: inputShadow,
                borderRadius: '16px',
                padding: '8px',
                gap: '4px',
              }}
            >
              <input
                ref={inputRef}
                type="text"
                value={inputMessage}
                onChange={e => setInputMessage(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={t('rules.chatbot.inputPlaceholder', 'Select one option above or type your answer')}
                disabled={status === 'rules_proposed' || status === 'rules_created' || status === 'completed' || isStarting}
                className="flex-1 outline-none chatbot-input"
                style={{
                  background: 'transparent',
                  color: titleColor,
                  fontSize: '14px',
                  lineHeight: '20px',
                  padding: '8px 12px',
                  border: 'none',
                }}
                maxLength={2000}
              />
              <button
                onClick={handleSendMessage}
                disabled={!canSend}
                className="flex items-center justify-center flex-shrink-0 transition-opacity"
                style={{
                  width: '36px',
                  height: '36px',
                  background: 'linear-gradient(90deg, #2B7FFF 0%, #AD46FF 100%)',
                  borderRadius: '8px',
                  opacity: canSend ? 1 : 0.5,
                  cursor: canSend ? 'pointer' : 'default',
                  border: 'none',
                }}
              >
                <Send size={20} color="#FFFFFF" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Rules Required Modal — shown when redirected from Dashboard with 0 rules */}
      {showRulesRequiredModal && createPortal(
        <div
          className="fixed inset-0 flex items-center justify-center z-50 p-4"
          style={{ background: isDark ? 'rgba(0,0,0,0.7)' : 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }}
        >
          <div
            className="rounded-2xl w-full max-w-md overflow-hidden"
            style={{ background: mainBg, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}
          >
            <div className="flex flex-col items-center text-center px-8 py-10 gap-4">
              <div
                className="w-14 h-14 rounded-xl flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, #2B7FFF, #AD46FF)' }}
              >
                <Zap size={28} color="#FFFFFF" />
              </div>
              <h3 className="text-lg font-bold" style={{ color: titleColor }}>
                {t('rules.chatbot.rulesRequiredTitle', 'Set up your first rule')}
              </h3>
              <p className="text-sm" style={{ color: subtitleColor }}>
                {t('rules.chatbot.rulesRequiredDescription', 'Please add at least one rule to access your dashboard. Describe your growth strategy below and our AI will create rules for you.')}
              </p>
              <button
                onClick={() => setShowRulesRequiredModal(false)}
                className="w-full text-white font-medium transition-opacity hover:opacity-90"
                style={{
                  padding: '12px 16px',
                  background: 'linear-gradient(90deg, #2B7FFF 0%, #AD46FF 100%)',
                  borderRadius: '12px',
                  fontSize: '15px',
                  border: 'none',
                  cursor: 'pointer',
                  marginTop: '4px',
                }}
              >
                {t('rules.chatbot.getStarted', 'Get started')}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default NewRule;
